import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SectionTabs from '../components/SectionTabs';
import { CalendarDays, FilePlus, Mail, Package } from 'lucide-react';

function PhysicalUploadPage() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [months, setMonths] = useState([]);
  const [deleteMonth, setDeleteMonth] = useState('');

  const fetchMonths = async () => {
    try {
      const res = await axios.get('/api/physical-sales/months');
      setMonths(res.data);
      if (res.data.length > 0 && !deleteMonth) setDeleteMonth(res.data[0]);
    } catch (e) {}
  };

  useEffect(() => { fetchMonths(); }, []);

  const handleUpload = async () => {
    if (!file) return alert('파일을 선택해주세요.');
    setLoading(true);
    setResult(null);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/physical-sales/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setFile(null);
      fetchMonths();
    } catch (e) {
      setUploadError(e.response?.data?.errorMessage || '업로드 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteMonth) return alert('삭제할 월을 선택해주세요.');
    if (!window.confirm(`${deleteMonth} 음반 정산 데이터를 모두 삭제하고 재고를 복구하시겠습니까?`)) return;
    try {
      await axios.delete('/api/physical-sales', { data: { calculateMonth: deleteMonth } });
      alert('삭제 완료');
      setResult(null);
      fetchMonths();
    } catch (e) {
      alert(e.response?.data?.errorMessage || '삭제 오류');
    }
  };

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">음반 정산 업로드</div>
      </div>

      <SectionTabs items={[
        { label: '음원 월정산', to: '/MonthlyUpload', icon: CalendarDays },
        { label: '음반 월정산', to: '/PhysicalUpload', icon: Package },
        { label: '대량등록', to: '/BigDataUpload', icon: FilePlus },
        { label: '권리사메일발송', to: '/EmailToClients', icon: Mail },
      ]} />

      {/* 업로드 */}
      <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#182334] p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-300">엑셀 업로드</h3>
        <p className="text-xs text-slate-500">엑셀 컬럼: <span className="text-slate-400">판매처 | 판매일자 | 품번 | 품목 | 수량 | 판매금액</span></p>
        <input
          type="file"
          accept=".xls,.xlsx"
          onChange={(e) => setFile(e.target.files[0])}
          className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sky-500/10 file:text-sky-400 hover:file:bg-sky-500/20 hover:cursor-pointer"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={handleUpload}
            disabled={loading || !file}
            className="px-4 py-2 bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 text-sm font-semibold rounded-xl disabled:opacity-50 transition-colors">
            {loading ? '처리 중...' : '업로드'}
          </button>
          <button
            onClick={() => {
              const link = document.createElement('a');
              link.href = 'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/download/PhysicalSalesUpload.xlsx';
              link.download = 'PhysicalSalesUpload.xlsx';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-slate-300 text-sm font-semibold transition-colors">
            양식 다운로드
          </button>
        </div>

        {/* 업로드 에러 */}
        {uploadError && (
          <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3">
            <span className="text-rose-400 text-lg mt-0.5">⚠️</span>
            <div>
              <div className="text-sm font-semibold text-rose-400 mb-1">업로드 실패</div>
              <div className="text-xs text-rose-300">{uploadError}</div>
            </div>
            <button onClick={() => setUploadError(null)} className="ml-auto text-rose-400 hover:text-rose-200 text-lg leading-none">✕</button>
          </div>
        )}

        {/* 결과 */}
        {result && (
          <div className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <div className="text-sm text-emerald-400 font-semibold">{result.saved}건 처리 완료</div>
            {result.errors?.length > 0 && (
              <div className="mt-2 space-y-1">
                {result.errors.map((err, i) => (
                  <div key={i} className="text-xs text-rose-400">{err}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 삭제 */}
      <div className="mt-4 rounded-2xl border border-white/[0.08] bg-[#182334] p-6 space-y-3">
        <h3 className="text-sm font-semibold text-slate-300">월별 데이터 삭제</h3>
        <div className="flex items-center gap-3">
          <select
            value={deleteMonth}
            onChange={(e) => setDeleteMonth(e.target.value)}
            className="bg-[#0d1526] text-white rounded-lg px-3 py-2 text-sm border border-white/10">
            {months.map((m) => <option key={m} value={m}>{m.slice(0, 7)}</option>)}
          </select>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 text-sm font-semibold rounded-xl transition-colors">
            데이터 삭제 (재고 복구)
          </button>
        </div>
        <p className="text-xs text-slate-500">삭제 시 해당 월 판매 수량만큼 재고가 복구됩니다.</p>
      </div>
    </>
  );
}

export default PhysicalUploadPage;
